import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { M16_ROLES, hasAnyRole } from './lib/roles';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { GrievanceDetail } from './pages/grievances/GrievanceDetail';
import { GrievancesHome } from './pages/grievances/GrievancesHome';
import { ConciliationDetail } from './pages/conciliation/ConciliationDetail';
import { ConciliationHome } from './pages/conciliation/ConciliationHome';
import { SettlementDetail } from './pages/settlements/SettlementDetail';
import { TribunalCaseDetail } from './pages/tribunal/TribunalCaseDetail';
import { TribunalCases } from './pages/tribunal/TribunalCases';
import { InspectionDetail } from './pages/inspections/InspectionDetail';
import { InspectionsHome } from './pages/inspections/InspectionsHome';
import { OshIncidentDetail } from './pages/osh/OshIncidentDetail';
import { OshIncidentsHome } from './pages/osh/OshIncidentsHome';
import { EmployerRatio } from './pages/ratio/EmployerRatio';
import { RatioDashboard } from './pages/ratio/RatioDashboard';
import { Shell, type NavItem } from './ui/Shell';

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  const roles = persona.roles;
  const isOfficer = roles.includes(M16_ROLES.LABOUR_OFFICER);
  const isMediator = roles.includes(M16_ROLES.MEDIATOR);
  const isInspector = roles.includes(M16_ROLES.LABOUR_INSPECTOR);
  const isChief = roles.includes(M16_ROLES.CHIEF_INSPECTOR);

  const items: NavItem[] = [{ to: '/grievances', label: 'Grievances', icon: '⚖' }];
  if (isOfficer || isMediator) items.push({ to: '/conciliation', label: 'Conciliation', icon: '⚖' });
  if (isOfficer || isChief) items.push({ to: '/tribunal', label: 'Tribunal Cases', icon: '⌘' });
  if (isOfficer || isInspector || isChief) items.push({ to: '/inspections', label: 'Workplace Inspections', icon: '☷' });
  if (isInspector || isChief || persona.kind === 'employer') items.push({ to: '/osh-incidents', label: 'OSH Incidents', icon: '⚠' });
  if (isOfficer || isChief || persona.kind === 'employer') items.push({ to: '/ratio', label: 'Ratio Monitoring', icon: '⌗' });

  // Land each persona somewhere useful for their primary duty rather than
  // always defaulting to the same screen.
  const landing = isMediator
    ? '/conciliation'
    : isInspector || isChief
      ? '/inspections'
      : '/grievances';

  return (
    <Routes>
      <Route element={<Shell title="Labour Relations & Compliance" items={items} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="grievances" element={<GrievancesHome />} />
        <Route path="grievances/:id" element={<GrievanceDetail />} />

        <Route
          path="conciliation"
          element={hasAnyRole(roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.MEDIATOR) ? <ConciliationHome /> : <Navigate to={landing} replace />}
        />
        <Route path="conciliation/:id" element={<ConciliationDetail />} />

        <Route path="settlements/:id" element={<SettlementDetail />} />

        <Route
          path="tribunal"
          element={hasAnyRole(roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.CHIEF_INSPECTOR) ? <TribunalCases /> : <Navigate to={landing} replace />}
        />
        <Route path="tribunal/:id" element={<TribunalCaseDetail />} />

        <Route path="inspections" element={<InspectionsHome />} />
        <Route path="inspections/:id" element={<InspectionDetail />} />

        <Route path="osh-incidents" element={<OshIncidentsHome />} />
        <Route path="osh-incidents/:id" element={<OshIncidentDetail />} />

        <Route path="ratio" element={<RatioDashboard />} />
        <Route path="ratio/:employerEntityId" element={<EmployerRatio />} />

        <Route path="*" element={<Navigate to={landing} replace />} />
      </Route>
    </Routes>
  );
}
